import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  FileText,
  Clock,
  Package,
  CreditCard,
  FileMinus,
  FileCheck,
  DollarSign,
  Receipt,
  Sliders,
  Truck,
  Search,
  Eye,
  Edit,
  Check,
  X,
  Filter,
  Download,
  Mail,
  Printer,
  RefreshCw,
  Send,
} from "lucide-react";
import BackButton from "../../components/backbutton";
import ScreenHeader from "../../components/ScreenHeader";
import Pagination from "../../components/Pagination";
import "./accountmanager.css";

export default function AccountManagerDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("list-po");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    paymentMode: "", paymentDate: "", utrNumber: "", verified: false
  });

  const itemsPerPage = 6;
  const [currentPOPage, setCurrentPOPage] = useState(1);
  const [currentPendingPOPage, setCurrentPendingPOPage] = useState(1);
  const [currentGRNPage, setCurrentGRNPage] = useState(1);
  const [currentSupplierPaymentPage, setCurrentSupplierPaymentPage] = useState(1);
  const [currentCustomerInvoicePage, setCurrentCustomerInvoicePage] = useState(1);
  const [currentCreditNotePage, setCurrentCreditNotePage] = useState(1);
  const [currentDebitNotePage, setCurrentDebitNotePage] = useState(1);
  const [currentInvoicePage, setCurrentInvoicePage] = useState(1);

  const resetPagination = () => {
    setCurrentPOPage(1);
    setCurrentPendingPOPage(1);
    setCurrentGRNPage(1);
    setCurrentSupplierPaymentPage(1);
    setCurrentCustomerInvoicePage(1);
    setCurrentCreditNotePage(1);
    setCurrentDebitNotePage(1);
    setCurrentInvoicePage(1);
  };

  // Button styles
  const buttonStyles = {
    view: { backgroundColor: '#E0E7FF', color: '#4F46E5' },
    edit: { backgroundColor: '#FEF3C7', color: '#D97706' },
    approve: { backgroundColor: '#D1FAE5', color: '#059669' },
    reject: { backgroundColor: '#FEE2E2', color: '#DC2626' },
    newInv: { backgroundColor: '#FEF3C7', color: '#D97706' },
    revise: { backgroundColor: '#E0E7FF', color: '#4F46E5' },
    mail: { backgroundColor: '#DBEAFE', color: '#2563EB' },
    download: { backgroundColor: '#F3E8FF', color: '#7C3AED' },
    print: { backgroundColor: '#FCE7F3', color: '#BE185D' },
    payment: { backgroundColor: '#D1FAE5', color: '#059669' },
  };

  // Action Button Component with Tooltip
  const ActionButton = ({ onClick, type, icon: Icon, tooltip }) => {
    const style = buttonStyles[type] || buttonStyles.view;
    return (
      <div style={{ position: 'relative', display: 'inline-flex' }}>
        <button
          onClick={onClick}
          style={{
            width: '32px', height: '32px', padding: '0',
            border: 'none', borderRadius: '6px', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: style.backgroundColor, color: style.color,
            transition: 'none', flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = style.backgroundColor;
            e.currentTarget.style.color = style.color;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = style.backgroundColor;
            e.currentTarget.style.color = style.color;
          }}
        >
          <Icon size={16} />
        </button>
        <span style={{
          position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%',
          transform: 'translateX(-50%)', backgroundColor: '#1F2937', color: '#ffffff',
          padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 500,
          whiteSpace: 'nowrap', opacity: 0, visibility: 'hidden', zIndex: 1001, pointerEvents: 'none',
        }} className="tooltip-span">
          {tooltip}
        </span>
      </div>
    );
  };

  // Sample Data
  const [poList] = useState([
    { id: "PO-001", poNumber: "PO-2024-001", customer: "ABC Corp", supplier: "ABC Suppliers", product: "Raw Material A", quantity: 100, amount: "₹1,25,000", date: "2024-03-15", status: "Approved" },
    { id: "PO-002", poNumber: "PO-2024-002", customer: "XYZ Ltd", supplier: "XYZ Enterprises", product: "Machine Parts", quantity: 50, amount: "₹85,000", date: "2024-03-14", status: "Pending" },
    { id: "PO-003", poNumber: "PO-2024-003", customer: "PQR Industries", supplier: "Tech Solutions", product: "Laptops", quantity: 10, amount: "₹2,50,000", date: "2024-03-13", status: "Approved" },
    { id: "PO-004", poNumber: "PO-2024-004", customer: "LMN Corp", supplier: "Global Traders", product: "Steel Sheets", quantity: 200, amount: "₹3,00,000", date: "2024-03-12", status: "Pending" },
    { id: "PO-005", poNumber: "PO-2024-005", customer: "DEF Ltd", supplier: "Global Traders", product: "Steel Sheets", quantity: 150, amount: "₹3,00,000", date: "2024-03-11", status: "Pending" },
    { id: "PO-006", poNumber: "PO-2024-006", customer: "GHI Ind", supplier: "Prime Plastics", product: "Plastic Components", quantity: 300, amount: "₹1,50,000", date: "2024-03-10", status: "Approved" },
  ]);

  const [pendingPOs, setPendingPOs] = useState([
    { id: "PPO-001", poNumber: "PO-2024-002", customer: "XYZ Ltd", supplier: "XYZ Enterprises", product: "Machine Parts", quantity: 50, amount: "₹85,000", excess: 0, date: "2024-03-14", status: "Pending", approvedByMD: false, invoiceChecked: false, quantityPending: 50 },
    { id: "PPO-002", poNumber: "PO-2024-004", customer: "ABC Corp", supplier: "Global Traders", product: "Steel Sheets", quantity: 200, amount: "₹3,00,000", excess: 10, date: "2024-03-16", status: "Pending", approvedByMD: false, invoiceChecked: false, quantityPending: 200 },
    { id: "PPO-003", poNumber: "PO-2024-005", customer: "LMN Corp", supplier: "Prime Plastics", product: "Plastic Components", quantity: 150, amount: "₹45,000", excess: 5, date: "2024-03-17", status: "Pending", approvedByMD: true, invoiceChecked: false, quantityPending: 150 },
    { id: "PPO-004", poNumber: "PO-2024-006", customer: "DEF Ltd", supplier: "Elite Electronics", product: "Sensors", quantity: 80, amount: "₹1,20,000", excess: 0, date: "2024-03-18", status: "Pending", approvedByMD: false, invoiceChecked: true, quantityPending: 80 }
  ]);

  const [grnList] = useState([
    { id: "GRN-001", grnNumber: "GRN-2024-001", supplier: "ABC Suppliers", product: "Raw Material A", quantity: 100, supplierInvoiceNo: "INV-001", date: "2024-03-16", status: "Pending" },
    { id: "GRN-002", grnNumber: "GRN-2024-002", supplier: "XYZ Enterprises", product: "Machine Parts", quantity: 50, supplierInvoiceNo: "INV-002", date: "2024-03-15", status: "Completed" },
    { id: "GRN-003", grnNumber: "GRN-2024-003", supplier: "Tech Solutions", product: "Laptops", quantity: 10, supplierInvoiceNo: "INV-003", date: "2024-03-14", status: "Pending" },
    { id: "GRN-004", grnNumber: "GRN-2024-004", supplier: "Global Traders", product: "Steel Sheets", quantity: 200, supplierInvoiceNo: "INV-004", date: "2024-03-13", status: "Completed" }
  ]);

  const [supplierPayments] = useState([
    { id: "PAY-001", grnNumber: "GRN-2024-001", supplier: "ABC Suppliers", product: "Raw Material A", quantity: 100, supplierInvoiceNo: "INV-001", amount: "₹1,25,000", status: "Pending" },
    { id: "PAY-002", grnNumber: "GRN-2024-002", supplier: "XYZ Enterprises", product: "Machine Parts", quantity: 50, supplierInvoiceNo: "INV-002", amount: "₹85,000", status: "Paid" },
    { id: "PAY-003", grnNumber: "GRN-2024-003", supplier: "Tech Solutions", product: "Laptops", quantity: 10, supplierInvoiceNo: "INV-003", amount: "₹2,50,000", status: "Pending" },
    { id: "PAY-004", grnNumber: "GRN-2024-004", supplier: "Global Traders", product: "Steel Sheets", quantity: 200, supplierInvoiceNo: "INV-004", amount: "₹3,00,000", status: "Pending" }
  ]);

  const [customerInvoices] = useState([
    { id: "INV-001", invoiceNumber: "INV-2024-001", customer: "ABC Corp", amount: "₹1,50,000", cgst: "₹13,500", sgst: "₹13,500", igst: "₹0", date: "2024-03-10", status: "Pending" },
    { id: "INV-002", invoiceNumber: "INV-2024-002", customer: "XYZ Ltd", amount: "₹2,00,000", cgst: "₹18,000", sgst: "₹18,000", igst: "₹0", date: "2024-03-12", status: "Partial" },
    { id: "INV-003", invoiceNumber: "INV-2024-003", customer: "PQR Industries", amount: "₹1,75,000", cgst: "₹0", sgst: "₹0", igst: "₹31,500", date: "2024-03-14", status: "Pending" },
    { id: "INV-004", invoiceNumber: "INV-2024-004", customer: "LMN Corp", amount: "₹3,00,000", cgst: "₹27,000", sgst: "₹27,000", igst: "₹0", date: "2024-03-15", status: "Pending" }
  ]);

  const [creditNotes, setCreditNotes] = useState([
    { id: "CN-001", creditNoteNumber: "CN-2024-001", supplier: "ABC Suppliers", grnNumber: "GRN-2024-001", product: "Raw Material A", quantity: 10, amount: "₹12,500", reason: "Damaged goods received", date: "2024-03-15", status: "Pending" },
    { id: "CN-002", creditNoteNumber: "CN-2024-002", supplier: "XYZ Enterprises", grnNumber: "GRN-2024-002", product: "Machine Parts", quantity: 5, amount: "₹8,500", reason: "Quantity shortage", date: "2024-03-14", status: "Pending" },
    { id: "CN-003", creditNoteNumber: "CN-2024-003", supplier: "Tech Solutions", grnNumber: "GRN-2024-003", product: "Laptops", quantity: 2, amount: "₹1,00,000", reason: "Defective products returned", date: "2024-03-12", status: "Completed" },
    { id: "CN-004", creditNoteNumber: "CN-2024-004", supplier: "Global Traders", grnNumber: "GRN-2024-004", product: "Steel Sheets", quantity: 8, amount: "₹24,000", reason: "Quality issues", date: "2024-03-10", status: "Pending" }
  ]);

  const [debitNotes, setDebitNotes] = useState([
    { id: "DN-001", debitNoteNumber: "DN-2024-001", customer: "ABC Corp", invoiceNumber: "INV-2024-001", product: "Finished Product A", quantity: 15, amount: "₹22,500", reason: "Extra quantity supplied", date: "2024-03-15", status: "Pending" },
    { id: "DN-002", debitNoteNumber: "DN-2024-002", customer: "XYZ Ltd", invoiceNumber: "INV-2024-002", product: "Machine Parts", quantity: 8, amount: "₹13,600", reason: "Price difference adjustment", date: "2024-03-13", status: "Pending" },
    { id: "DN-003", debitNoteNumber: "DN-2024-003", customer: "PQR Industries", invoiceNumber: "INV-2024-003", product: "Electronics", quantity: 3, amount: "₹5,250", reason: "GST calculation error", date: "2024-03-10", status: "Completed" },
    { id: "DN-004", debitNoteNumber: "DN-2024-004", customer: "LMN Corp", invoiceNumber: "INV-2024-004", product: "Finished Product B", quantity: 10, amount: "₹30,000", reason: "Quantity short supplied", date: "2024-03-08", status: "Pending" }
  ]);

  const [dispatchData] = useState({
    customers: ["ABC Corp", "XYZ Ltd", "PQR Industries"],
    schedules: ["SCH-001", "SCH-002", "SCH-003"],
    scheduledList: [{ product: "Product A", quantity: 50 }, { product: "Product B", quantity: 30 }],
    finishedGoods: [{ product: "Finished Product 1", stock: 200 }, { product: "Finished Product 2", stock: 150 }]
  });

  const [listInvoices] = useState([
    { id: "INV-001", invoiceNumber: "INV-2024-001", customer: "ABC Corp", schedule: "SCH-001", date: "2024-03-10", productGroup: "Electronics", amount: "₹1,50,000", status: "Paid" },
    { id: "INV-002", invoiceNumber: "INV-2024-002", customer: "XYZ Ltd", schedule: "SCH-002", date: "2024-03-12", productGroup: "Machinery", amount: "₹2,00,000", status: "Pending" },
    { id: "INV-003", invoiceNumber: "INV-2024-003", customer: "PQR Industries", schedule: "SCH-003", date: "2024-03-14", productGroup: "Electronics", amount: "₹1,75,000", status: "Paid" },
    { id: "INV-004", invoiceNumber: "INV-2024-004", customer: "LMN Corp", schedule: "SCH-001", date: "2024-03-16", productGroup: "Machinery", amount: "₹3,00,000", status: "Pending" }
  ]);

  // Sidebar menu items
  const tabItems = [
    { id: "list-po", label: "List PO", icon: <FileText size={18} /> },
    { id: "pending-po", label: "Pending PO", icon: <Clock size={18} /> },
    { id: "finished-goods", label: "Finished Goods", icon: <Package size={18} /> },
    { id: "credit-note", label: "Credit Note", icon: <CreditCard size={18} /> },
    { id: "debit-note", label: "Debit Note", icon: <FileMinus size={18} /> },
    { id: "grn", label: "Goods Received Note (GRN) Generation", icon: <FileCheck size={18} /> },
    { id: "supplier-payment", label: "Payment Voucher", icon: <DollarSign size={18} /> },
    { id: "customer-payment", label: "Expenditure Voucher", icon: <Receipt size={18} /> },
    { id: "other-slips", label: "Other Slips (Add/Edit)", icon: <Sliders size={18} /> },
    { id: "dispatch", label: "Dispatch", icon: <Truck size={18} /> },
    { id: "list-invoice", label: "List of Invoice", icon: <FileText size={18} /> }
  ];

  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleConfirmLogout = () => { setShowLogoutModal(false); onLogout(); navigate('/login'); };
  const handleCancelLogout = () => setShowLogoutModal(false);

  const filteredData = (data, searchFields) => {
    if (!searchTerm) return data;
    return data.filter(item => searchFields.some(field => item[field]?.toString().toLowerCase().includes(searchTerm.toLowerCase())));
  };

  const handleApprovePO = (poId) => {
    setPendingPOs(pendingPOs.map(po => po.id === poId ? { ...po, approvedByMD: true } : po));
    alert(`PO ${poId} approved!`);
  };
  const handleCheckInvoice = (poId) => {
    setPendingPOs(pendingPOs.map(po => po.id === poId ? { ...po, invoiceChecked: true } : po));
    alert(`Invoice checked for PO ${poId}`);
  };
  const handleRejectPO = (poId) => {
    setPendingPOs(pendingPOs.filter(po => po.id !== poId));
    alert(`PO ${poId} rejected.`);
  };

  const handleSubmitPayment = () => {
    if (activeTab === 'credit-note' && selectedItem) {
      setCreditNotes(creditNotes.map(c => 
        c.id === selectedItem.id ? { ...c, status: "Completed", paymentMode: paymentDetails.paymentMode, paymentDate: paymentDetails.paymentDate, utrNumber: paymentDetails.utrNumber, verified: paymentDetails.verified } : c
      ));
    } else if (activeTab === 'debit-note' && selectedItem) {
      setDebitNotes(debitNotes.map(d => 
        d.id === selectedItem.id ? { ...d, status: "Completed", paymentMode: paymentDetails.paymentMode, paymentDate: paymentDetails.paymentDate, utrNumber: paymentDetails.utrNumber, verified: paymentDetails.verified } : d
      ));
    }
    alert('Payment submitted successfully!');
    setShowPaymentModal(false);
    setPaymentDetails({ paymentMode: "", paymentDate: "", utrNumber: "", verified: false });
    setSelectedItem(null);
  };

  // List PO Component
  const ListPOContent = () => {
    const filtered = filteredData(poList, ['poNumber', 'customer', 'supplier', 'product']);
    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const currentItems = filtered.slice((currentPOPage - 1) * itemsPerPage, currentPOPage * itemsPerPage);
    return (
      <div className="am-content-inner">
        <div className="filter-bar">
          <Search size={18} />
          <input type="text" placeholder="Search PO..." className="search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div className="po-cards-grid">
          {currentItems.map(po => (
            <div key={po.id} className="po-card">
              <div className="po-card-header">
                <h3>{po.poNumber}</h3>
                <span className={`status ${po.status === 'Approved' ? 'approved' : 'pending'}`}>{po.status}</span>
              </div>
              <div className="po-card-details">
                <div className="detail-item"><label>Customer</label><span>{po.customer}</span></div>
                <div className="detail-item"><label>Supplier</label><span>{po.supplier}</span></div>
                <div className="detail-item"><label>Product</label><span>{po.product}</span></div>
                <div className="detail-item"><label>Raw Material</label><span>{po.product}</span></div>
                <div className="detail-item"><label>Quantity</label><span>{po.quantity}</span></div>
                <div className="detail-item"><label>Amount</label><span className="amount">{po.amount}</span></div>
                <div className="detail-item" style={{ gridColumn: 'span 2' }}><label>Date</label><span>{po.date}</span></div>
              </div>
            </div>
          ))}
        </div>
        <Pagination currentPage={currentPOPage} totalPages={totalPages} onPageChange={setCurrentPOPage} pageSize={itemsPerPage} totalItems={filtered.length} />
      </div>
    );
  };

  // Pending PO Component
  const PendingPOContent = () => {
    const totalPages = Math.ceil(pendingPOs.length / itemsPerPage);
    const currentItems = pendingPOs.slice((currentPendingPOPage - 1) * itemsPerPage, currentPendingPOPage * itemsPerPage);
    return (
      <div className="am-content-inner">
        <div className="table-wrapper">
          <table className="am-table">
            <thead>
              <tr>
                <th>PO Number</th><th>Customer</th><th>Supplier</th><th>Product</th>
                <th>Qty</th><th>Amount</th><th>Excess</th><th>MD Approval</th>
                <th>Invoice</th><th>Qty Pending</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map(po => (
                <tr key={po.id}>
                  <td className="highlight">{po.poNumber}</td>
                  <td>{po.customer}</td>
                  <td>{po.supplier}</td>
                  <td>{po.product}</td>
                  <td>{po.quantity}</td>
                  <td className="amount">{po.amount}</td>
                  <td>{po.excess > 0 ? `+${po.excess}` : '0'}</td>
                  <td>
                    <span className={`status ${po.approvedByMD ? 'approved' : 'pending'}`}>
                      {po.approvedByMD ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>
                    <span className={`status ${po.invoiceChecked ? 'approved' : 'pending'}`}>
                      {po.invoiceChecked ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>{po.quantityPending}</td>
                  <td>
                    <div className="action-buttons">
                      <ActionButton onClick={() => alert(`PO Details: ${po.poNumber}`)} type="view" icon={Eye} tooltip="View Details" />
                      <ActionButton onClick={() => handleApprovePO(po.id)} type="approve" icon={Check} tooltip="Approve PO" />
                      <ActionButton onClick={() => handleCheckInvoice(po.id)} type="edit" icon={FileCheck} tooltip="Check Invoice" />
                      <ActionButton onClick={() => alert(`Request new invoice for ${po.poNumber}`)} type="newInv" icon={Mail} tooltip="Request New Invoice" />
                      <ActionButton onClick={() => alert(`Revise invoice for ${po.poNumber}`)} type="revise" icon={RefreshCw} tooltip="Revise Invoice" />
                      <ActionButton onClick={() => handleRejectPO(po.id)} type="reject" icon={X} tooltip="Reject PO" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination currentPage={currentPendingPOPage} totalPages={totalPages} onPageChange={setCurrentPendingPOPage} pageSize={itemsPerPage} totalItems={pendingPOs.length} />
      </div>
    );
  };

  // GRN Component
  const GRNContent = () => {
    const totalPages = Math.ceil(grnList.length / itemsPerPage);
    const currentItems = grnList.slice((currentGRNPage - 1) * itemsPerPage, currentGRNPage * itemsPerPage);
    return (
      <div className="am-content-inner">
        <div className="table-wrapper">
          <table className="am-table">
            <thead>
              <tr><th>GRN Number</th><th>Supplier</th><th>Product</th><th>Qty</th><th>Supplier Invoice</th><th>Date</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {currentItems.map(grn => (
                <tr key={grn.id}>
                  <td className="highlight">{grn.grnNumber}</td><td>{grn.supplier}</td><td>{grn.product}</td>
                  <td>{grn.quantity}</td><td>{grn.supplierInvoiceNo}</td><td>{grn.date}</td>
                  <td><span className={`status ${grn.status === 'Completed' ? 'approved' : 'pending'}`}>{grn.status}</span></td>
                  <td>
                    <div className="action-buttons">
                      <ActionButton onClick={() => alert(`Edit GRN ${grn.grnNumber}`)} type="edit" icon={Edit} tooltip="Edit" />
                      <ActionButton onClick={() => alert(`GRN submitted!`)} type="approve" icon={Send} tooltip="Submit" />
                      <ActionButton onClick={() => alert(`Email sent!`)} type="mail" icon={Mail} tooltip="Auto Mail" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination currentPage={currentGRNPage} totalPages={totalPages} onPageChange={setCurrentGRNPage} pageSize={itemsPerPage} totalItems={grnList.length} />
        <div className="summary-stats">
          <div className="stat"><strong>PO</strong><br/>{poList.length}</div>
          <div className="stat"><strong>GRN</strong><br/>{grnList.length}</div>
          <div className="stat"><strong>Pending</strong><br/>{grnList.filter(g => g.status === 'Pending').length}</div>
        </div>
      </div>
    );
  };

  // Supplier Payment Component
  const SupplierPaymentContent = () => {
    const filtered = filteredData(supplierPayments, ['grnNumber', 'supplier', 'supplierInvoiceNo']);
    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const currentItems = filtered.slice((currentSupplierPaymentPage - 1) * itemsPerPage, currentSupplierPaymentPage * itemsPerPage);
    return (
      <div className="am-content-inner">
        <div className="filter-bar">
          <Search size={18} />
          <input type="text" placeholder="Search by GRN, Supplier..." className="search-input" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); resetPagination(); }} />
          <button className="filter-btn" onClick={() => setFilterOpen(!filterOpen)}><Filter size={16} /> Filter</button>
        </div>
        {filterOpen && (
          <div className="filter-panel">
            <select className="filter-select"><option value="">All Suppliers</option><option>ABC Suppliers</option><option>XYZ Enterprises</option></select>
            <select className="filter-select"><option value="">All Status</option><option>Pending</option><option>Paid</option></select>
            <button className="apply-filter">Apply</button>
          </div>
        )}
        <div className="table-wrapper">
          <table className="am-table">
            <thead>
              <tr><th>GRN Number</th><th>Supplier</th><th>Product</th><th>Qty</th><th>Supplier Invoice</th><th>Amount</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {currentItems.map(p => (
                <tr key={p.id}>
                  <td className="highlight">{p.grnNumber}</td><td>{p.supplier}</td><td>{p.product}</td>
                  <td>{p.quantity}</td><td>{p.supplierInvoiceNo}</td><td className="amount">{p.amount}</td>
                  <td><span className={`status ${p.status === 'Paid' ? 'approved' : 'pending'}`}>{p.status}</span></td>
                  <td>
                    <div className="action-buttons">
                      <ActionButton onClick={() => { setSelectedItem(p); setShowPaymentModal(true); }} type="edit" icon={Edit} tooltip="Edit" />
                      {p.status === 'Pending' && (
                        <ActionButton onClick={() => { setSelectedItem(p); setShowPaymentModal(true); }} type="payment" icon={DollarSign} tooltip="Make Payment" />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination currentPage={currentSupplierPaymentPage} totalPages={totalPages} onPageChange={setCurrentSupplierPaymentPage} pageSize={itemsPerPage} totalItems={filtered.length} />
      </div>
    );
  };

  // Customer Payment Component
  const CustomerPaymentContent = () => {
    const filtered = filteredData(customerInvoices, ['invoiceNumber', 'customer']);
    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const currentItems = filtered.slice((currentCustomerInvoicePage - 1) * itemsPerPage, currentCustomerInvoicePage * itemsPerPage);
    return (
      <div className="am-content-inner">
        <div className="filter-bar">
          <Search size={18} />
          <input type="text" placeholder="Search by Invoice #, Customer..." className="search-input" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); resetPagination(); }} />
        </div>
        <div className="table-wrapper">
          <table className="am-table">
            <thead>
              <tr><th>Invoice #</th><th>Customer</th><th>Invoice Value</th><th>CGST</th><th>SGST</th><th>IGST</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {currentItems.map(inv => (
                <tr key={inv.id}>
                  <td className="highlight">{inv.invoiceNumber}</td><td>{inv.customer}</td><td className="amount">{inv.amount}</td>
                  <td>{inv.cgst}</td><td>{inv.sgst}</td><td>{inv.igst}</td>
                  <td><span className={`status ${inv.status === 'Paid' ? 'approved' : inv.status === 'Partial' ? 'partial' : 'pending'}`}>{inv.status}</span></td>
                  <td>
                    <div className="action-buttons">
                      <ActionButton onClick={() => { setSelectedItem(inv); setShowPaymentModal(true); }} type="payment" icon={Receipt} tooltip="Receive Payment" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination currentPage={currentCustomerInvoicePage} totalPages={totalPages} onPageChange={setCurrentCustomerInvoicePage} pageSize={itemsPerPage} totalItems={filtered.length} />
      </div>
    );
  };

  // Credit Note Component
  const CreditNoteContent = () => {
    const filtered = filteredData(creditNotes, ['creditNoteNumber', 'supplier', 'grnNumber']);
    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const currentItems = filtered.slice((currentCreditNotePage - 1) * itemsPerPage, currentCreditNotePage * itemsPerPage);
    return (
      <div className="am-content-inner">
        <div className="table-wrapper">
          <table className="am-table">
            <thead>
              <tr><th>Credit Note #</th><th>Supplier</th><th>GRN #</th><th>Product</th><th>Qty</th><th>Amount</th><th>Reason</th><th>Date</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {currentItems.map(cn => (
                <tr key={cn.id}>
                  <td className="highlight">{cn.creditNoteNumber}</td><td>{cn.supplier}</td><td>{cn.grnNumber}</td>
                  <td>{cn.product}</td><td>{cn.quantity}</td><td className="amount">{cn.amount}</td>
                  <td>{cn.reason}</td><td>{cn.date}</td>
                  <td><span className={`status ${cn.status === 'Completed' ? 'approved' : 'pending'}`}>{cn.status}</span></td>
                  <td>
                    <div className="action-buttons">
                      <ActionButton onClick={() => alert(`Edit ${cn.creditNoteNumber}`)} type="edit" icon={Edit} tooltip="Edit" />
                      {cn.status === 'Pending' && (
                        <ActionButton onClick={() => { setSelectedItem(cn); setShowPaymentModal(true); }} type="payment" icon={DollarSign} tooltip="Make Payment" />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination currentPage={currentCreditNotePage} totalPages={totalPages} onPageChange={setCurrentCreditNotePage} pageSize={itemsPerPage} totalItems={filtered.length} />
      </div>
    );
  };

  // Debit Note Component
  const DebitNoteContent = () => {
    const filtered = filteredData(debitNotes, ['debitNoteNumber', 'customer', 'invoiceNumber']);
    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const currentItems = filtered.slice((currentDebitNotePage - 1) * itemsPerPage, currentDebitNotePage * itemsPerPage);
    return (
      <div className="am-content-inner">
        <div className="table-wrapper">
          <table className="am-table">
            <thead>
              <tr><th>Debit Note #</th><th>Customer</th><th>Invoice #</th><th>Product</th><th>Qty</th><th>Amount</th><th>Reason</th><th>Date</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {currentItems.map(dn => (
                <tr key={dn.id}>
                  <td className="highlight">{dn.debitNoteNumber}</td><td>{dn.customer}</td><td>{dn.invoiceNumber}</td>
                  <td>{dn.product}</td><td>{dn.quantity}</td><td className="amount">{dn.amount}</td>
                  <td>{dn.reason}</td><td>{dn.date}</td>
                  <td><span className={`status ${dn.status === 'Completed' ? 'approved' : 'pending'}`}>{dn.status}</span></td>
                  <td>
                    <div className="action-buttons">
                      <ActionButton onClick={() => alert(`Edit ${dn.debitNoteNumber}`)} type="edit" icon={Edit} tooltip="Edit" />
                      {dn.status === 'Pending' && (
                        <ActionButton onClick={() => { setSelectedItem(dn); setShowPaymentModal(true); }} type="payment" icon={Receipt} tooltip="Receive Payment" />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination currentPage={currentDebitNotePage} totalPages={totalPages} onPageChange={setCurrentDebitNotePage} pageSize={itemsPerPage} totalItems={filtered.length} />
      </div>
    );
  };

  // Dispatch Component
  const DispatchContent = () => (
    <div className="am-content-inner">
      <div className="dispatch-container">
        <div className="form-row-2col">
          <div className="form-group"><label>Select Customer</label><select className="form-input"><option value="">Select Customer</option>{dispatchData.customers.map(c => <option key={c}>{c}</option>)}</select></div>
          <div className="form-group"><label>Select Schedule</label><select className="form-input"><option value="">Select Schedule</option>{dispatchData.schedules.map(s => <option key={s}>{s}</option>)}</select></div>
        </div>
        <div className="dispatch-grid-2col">
          <div className="dispatch-card"><h4>Scheduled List</h4><table className="dispatch-table">{dispatchData.scheduledList.map((item, idx) => (<tr key={idx}><td>{item.product}</td><td>→</td><td>{item.quantity}</td></tr>))}</table></div>
          <div className="dispatch-card"><h4>Store Details - FG Stock</h4><table className="dispatch-table">{dispatchData.finishedGoods.map((item, idx) => (<tr key={idx}><td>{item.product}</td><td>→</td><td>{item.stock}</td></tr>))}</table></div>
        </div>
        <div className="dispatch-grid-2col">
          <div className="dispatch-card"><h4>Select FG</h4><table className="dispatch-table"><tr><td>Product 1</td><td>→</td><td><input type="number" placeholder="Qty" className="qty-input" /></td></tr><tr><td>Product 2</td><td>→</td><td><input type="number" placeholder="Qty" className="qty-input" /></td></tr></table></div>
          <div className="dispatch-card"><h4>Select Scrap</h4><table className="dispatch-table"><tr><td>Scrap 1</td><td>→</td><td><input type="number" placeholder="Qty" className="qty-input" /></td></tr></table></div>
        </div>
        <div className="dispatch-grid-2col">
          <div className="dispatch-card"><h4>Select Return</h4><table className="dispatch-table"><tr><td>Return 1</td><td>→</td><td><input type="number" placeholder="Qty" className="qty-input" /></td></tr></table></div>
          <div className="dispatch-card"><h4>Select NC</h4><table className="dispatch-table"><tr><td>NC 1</td><td>→</td><td><input type="number" placeholder="Qty" className="qty-input" /></td></tr></table></div>
        </div>
        <div className="dispatch-actions">
          <button className="btn-approve" onClick={() => alert("Dispatch completed!")}><Truck size={16} /> Process Dispatch</button>
        </div>
      </div>
    </div>
  );

  // List Invoices Component
  const ListInvoicesContent = () => {
    const filtered = filteredData(listInvoices, ['invoiceNumber', 'customer']);
    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const currentItems = filtered.slice((currentInvoicePage - 1) * itemsPerPage, currentInvoicePage * itemsPerPage);
    return (
      <div className="am-content-inner">
        <div className="filter-bar">
          <Search size={18} />
          <input type="text" placeholder="Search by Invoice #, Customer..." className="search-input" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); resetPagination(); }} />
          <button className="filter-btn" onClick={() => setFilterOpen(!filterOpen)}><Filter size={16} /> Filter</button>
        </div>
        {filterOpen && (
          <div className="filter-panel">
            <select className="filter-select"><option value="">Select Customer</option><option>ABC Corp</option><option>XYZ Ltd</option></select>
            <select className="filter-select"><option value="">Select Schedule</option><option>SCH-001</option><option>SCH-002</option></select>
            <input type="date" className="filter-input" />
            <select className="filter-select"><option value="">Product Group</option><option>Electronics</option><option>Machinery</option></select>
            <button className="apply-filter">Apply</button>
          </div>
        )}
        <div className="table-wrapper">
          <table className="am-table">
            <thead>
              <tr><th>Invoice #</th><th>Customer</th><th>Schedule</th><th>Date</th><th>Product Group</th><th>Amount</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {currentItems.map(inv => (
                <tr key={inv.id}>
                  <td className="highlight">{inv.invoiceNumber}</td><td>{inv.customer}</td><td>{inv.schedule}</td>
                  <td>{inv.date}</td><td>{inv.productGroup}</td><td className="amount">{inv.amount}</td>
                  <td><span className={`status ${inv.status === 'Paid' ? 'approved' : 'pending'}`}>{inv.status}</span></td>
                  <td>
                    <div className="action-buttons">
                      <ActionButton onClick={() => alert(`View ${inv.invoiceNumber}`)} type="view" icon={Eye} tooltip="View" />
                      <ActionButton onClick={() => alert(`Download ${inv.invoiceNumber}`)} type="download" icon={Download} tooltip="Download" />
                      <ActionButton onClick={() => alert(`Print ${inv.invoiceNumber}`)} type="print" icon={Printer} tooltip="Print" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination currentPage={currentInvoicePage} totalPages={totalPages} onPageChange={setCurrentInvoicePage} pageSize={itemsPerPage} totalItems={filtered.length} />
      </div>
    );
  };

  const PlaceholderContent = () => (
    <div className="am-content-inner" style={{ textAlign: 'center', padding: '48px' }}>
      <h2 style={{ color: '#4F46E5', margin: '0 0 8px 0' }}>Coming Soon</h2>
      <p style={{ color: '#6B7280' }}>This section is under development</p>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "list-po": return <ListPOContent />;
      case "pending-po": return <PendingPOContent />;
      case "finished-goods": return <PlaceholderContent />;
      case "credit-note": return <CreditNoteContent />;
      case "debit-note": return <DebitNoteContent />;
      case "grn": return <GRNContent />;
      case "supplier-payment": return <SupplierPaymentContent />;
      case "customer-payment": return <CustomerPaymentContent />;
      case "other-slips": return <PlaceholderContent />;
      case "dispatch": return <DispatchContent />;
      case "list-invoice": return <ListInvoicesContent />;
      default: return <PlaceholderContent />;
    }
  };

  return (
    <div className="am-dashboard-container">
      <div className="am-sidebar">
        <div className="am-logo-container">
          <div className="am-logo-icon"><img src="/rahulerp.jpg" alt="Logo" className="am-logo-image" /></div>
          <div><h2 className="am-company-name">Ragul Auto Tech</h2><p className="am-company-tagline">AI Powered ERP</p></div>
        </div>
        <nav className="am-nav-menu">
          {tabItems.map((tab) => (
            <button key={tab.id} className={`am-nav-item ${activeTab === tab.id ? 'am-nav-item-active' : ''}`} onClick={() => { setActiveTab(tab.id); resetPagination(); }}>
              <span className="am-nav-icon">{tab.icon}</span><span>{tab.label}</span>
            </button>
          ))}
        </nav>
        <div className="am-logout-container">
          <button onClick={handleLogoutClick} className="am-logout-button"><LogOut size={18} /> Logout</button>
        </div>
      </div>

      <div className="am-main-content">
        <ScreenHeader title="Accounts Dashboard" description={`Welcome, ${user?.role || 'Accounts Manager'}`} timestamp={new Date().toLocaleString()} containerClassName="am-header" titleClassName="am-main-title" descriptionClassName="am-subtitle" actionsClassName="am-header-actions" timestampClassName="am-last-updated" />
        <div className="am-back-button-wrapper"><BackButton /></div>
        <div className="am-scrollable-content">{renderContent()}</div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedItem && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Payment Details - {selectedItem.grnNumber || selectedItem.creditNoteNumber || selectedItem.debitNoteNumber || selectedItem.invoiceNumber}</h2>
              <button className="modal-close" onClick={() => setShowPaymentModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="details-grid">
                <div className="detail-item"><label>Supplier/Customer</label><span>{selectedItem.supplier || selectedItem.customer}</span></div>
                <div className="detail-item"><label>Amount</label><span className="amount">{selectedItem.amount}</span></div>
              </div>
              <div className="form-group"><label>Payment Mode</label><select className="form-input" value={paymentDetails.paymentMode} onChange={(e) => setPaymentDetails({...paymentDetails, paymentMode: e.target.value})}><option value="">Select</option><option>Bank Transfer</option><option>Cheque</option><option>Cash</option><option>UPI</option></select></div>
              <div className="form-group"><label>Payment Date</label><input type="date" className="form-input" value={paymentDetails.paymentDate} onChange={(e) => setPaymentDetails({...paymentDetails, paymentDate: e.target.value})} /></div>
              <div className="form-group"><label>UTR Number</label><input type="text" className="form-input" placeholder="Enter UTR number" value={paymentDetails.utrNumber} onChange={(e) => setPaymentDetails({...paymentDetails, utrNumber: e.target.value})} /></div>
              <div className="form-group checkbox"><label><input type="checkbox" checked={paymentDetails.verified} onChange={(e) => setPaymentDetails({...paymentDetails, verified: e.target.checked})} /> Verified</label></div>
            </div>
            <div className="modal-footer">
              <button className="btn-approve" onClick={handleSubmitPayment}><Check size={16} /> Submit Payment</button>
              <button className="btn-cancel" onClick={() => setShowPaymentModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Logout Modal */}
      {showLogoutModal && (
        <div className="logout-modal-overlay">
          <div className="logout-modal">
            <div className="logout-modal-header"><div className="logout-modal-icon">👋</div><h3>Confirm Logout</h3></div>
            <div className="logout-modal-body"><p>Are you sure you want to logout from <strong>Accounts</strong> dashboard?</p><p className="logout-modal-warning">You will be redirected to the login page.</p></div>
            <div className="logout-modal-footer">
              <button className="logout-modal-cancel" onClick={handleCancelLogout}>Cancel</button>
              <button className="logout-modal-confirm" onClick={handleConfirmLogout}>Yes, Logout</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}